import type { FormField, InterviewData } from '../../types';
import { getExportDateBounds, type CampaignExportFilters } from '../campaign-csv';
import { getExportFields, matchesExportAnswers } from '../campaign-export-filters';
import { isOtherChoiceAnswer } from '../choice-answers';
import { getStructuredResponseAnswers, type StructuredResponseAnswer } from '../response-completion';

/** The answers-only response projection the Overview reads. Never includes transcripts. */
export type AnalyticsResponse = Pick<InterviewData,
  'id' | 'response_number' | 'response_status' | 'created_at' | 'completed_at'
  | 'form_answers' | 'form_schema_snapshot' | 'conversation_progress' | 'calendar_bookings'>;

/** One answer category: `key` is the stored option value (or option id for image choice and ranking). */
export interface AnalyticsOption {
  key: string;
  label: string;
}

/** A question in the union of the current schema and every response snapshot. */
export interface AnalyticsQuestion {
  /** Stable field id. */
  id: string;
  /** Current schema label, else the first snapshot label. */
  label: string;
  /** Field type of the defining schema entry. */
  type: FormField['type'];
  /** Defining schema entry: the current field, else the first snapshot that has it. */
  field: FormField;
  /** Whether the field exists in the current schema. */
  current: boolean;
  /** Current options first, then snapshot-only options, then stored values matching no option. */
  options: AnalyticsOption[];
}

/** A typed answer value, one variant per family of stored shapes. */
export type ObservedValue =
  | { kind: 'choice'; keys: string[]; otherText?: string }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'number'; value: number }
  | { kind: 'text'; value: string }
  | { kind: 'ranking'; keys: string[] }
  | { kind: 'date'; value: string }
  | { kind: 'matrix'; selections: Record<string, string[]> }
  | { kind: 'address'; country: string }
  | { kind: 'present' };

/** One response's reading of one question. */
export type Observation =
  | { responseId: string; status: 'answered'; value: ObservedValue }
  | { responseId: string; status: 'missing' }
  | { responseId: string; status: 'excluded' };

/** Choice-like types whose stored answers are option values. */
const VALUE_CHOICE_TYPES = new Set(['choice_single', 'choice_multi']);
/** Types whose options are keyed by option id rather than value. */
const ID_OPTION_TYPES = new Set(['image_choice', 'ranking']);

/** Deterministic response order, matching the CSV export. */
function ordered(responses: readonly AnalyticsResponse[]): AnalyticsResponse[] {
  return [...responses].sort((a, b) => (a.response_number ?? 0) - (b.response_number ?? 0) || a.id.localeCompare(b.id));
}

/** Pre-snapshot responses used campaign field names; an empty snapshot is authoritative. */
function schemaOf(response: AnalyticsResponse, fields: FormField[]): FormField[] {
  return response.form_schema_snapshot ?? fields;
}

/** Options of a field as categories keyed the way answers store them. */
function fieldOptions(field: FormField): AnalyticsOption[] {
  if (!('options' in field) || !Array.isArray(field.options)) return [];
  return field.options.map(option => ({ key: ID_OPTION_TYPES.has(field.type) ? option.id : option.value, label: option.label }));
}

/** Raw stored choice values for one answer, including Other wrappers. */
function rawChoiceValues(raw: unknown): string[] {
  const value = isOtherChoiceAnswer(raw) ? raw.value : raw;
  if (typeof value === 'string') return [value];
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

/**
 * The union of the current schema and every response snapshot, keyed by field
 * id exactly as the CSV export builds its columns: current order first, then
 * snapshot-only fields. Information blocks are not questions.
 */
export function buildQuestionSet(fields: FormField[], responses: readonly AnalyticsResponse[]): AnalyticsQuestion[] {
  const rows = ordered(responses);
  const questions = new Map<string, AnalyticsQuestion>();
  const currentIds = new Set(fields.map(item => item.id));
  for (const schema of [fields, ...rows.map(row => row.form_schema_snapshot ?? [])]) {
    for (const item of schema) {
      if (item.type === 'information') continue;
      const existing = questions.get(item.id);
      if (!existing) {
        questions.set(item.id, { id: item.id, label: item.label, type: item.type, field: item, current: currentIds.has(item.id), options: fieldOptions(item) });
        continue;
      }
      for (const option of fieldOptions(item)) {
        if (!existing.options.some(known => known.key === option.key)) existing.options.push(option);
      }
    }
  }
  for (const question of questions.values()) {
    if (!VALUE_CHOICE_TYPES.has(question.type)) continue;
    for (const row of rows) {
      const snapshotField = schemaOf(row, fields).find(item => item.id === question.id);
      if (!snapshotField) continue;
      for (const key of rawChoiceValues(row.form_answers?.[snapshotField.name])) {
        if (!question.options.some(option => option.key === key)) question.options.push({ key, label: key });
      }
    }
  }
  return [...questions.values()];
}

/**
 * Responses in the filtered population: status, inclusive calendar dates in
 * the saved timezone, and answer conditions, with the same rules and matcher
 * as the CSV export so both always cover the same responses.
 */
export function filterPopulation(responses: readonly AnalyticsResponse[], fields: FormField[], filters: CampaignExportFilters): AnalyticsResponse[] {
  const { from, until } = getExportDateBounds(filters);
  const fromTime = from ? Date.parse(from) : undefined;
  const untilTime = until ? Date.parse(until) : undefined;
  return ordered(responses).filter(row => {
    if (filters.status === 'in_progress' && row.response_status && row.response_status !== 'in_progress') return false;
    if (filters.status && filters.status !== 'in_progress' && row.response_status !== filters.status) return false;
    const created = Date.parse(row.created_at);
    if (fromTime !== undefined && !(created >= fromTime)) return false;
    if (untilTime !== undefined && !(created < untilTime)) return false;
    return matchesExportAnswers(row, fields, filters);
  });
}

/** Value kinds by field object; snapshots repeat the same definitions across many responses. */
const kindCache = new WeakMap<FormField, string | undefined>();

/** Value kind used to detect a snapshot that changed a field's kind (export rule). */
function valueKind(item: FormField): string | undefined {
  if (!kindCache.has(item)) kindCache.set(item, getExportFields([item])[0]?.kind);
  return kindCache.get(item);
}

/** Convert one validated structured answer to a typed observation value. */
function toObserved(question: AnalyticsQuestion, answer: StructuredResponseAnswer): ObservedValue | undefined {
  const value = answer.value;
  switch (question.type) {
    case 'choice_single':
    case 'choice_multi': {
      const keys = rawChoiceValues(value);
      if (!keys.length) return undefined;
      return isOtherChoiceAnswer(value) ? { kind: 'choice', keys, otherText: value.otherText.trim() } : { kind: 'choice', keys };
    }
    case 'image_choice':
      return Array.isArray(value) && value.length ? { kind: 'choice', keys: value.filter((item): item is string => typeof item === 'string') } : undefined;
    case 'ranking':
      return Array.isArray(value) && value.length ? { kind: 'ranking', keys: value.filter((item): item is string => typeof item === 'string') } : undefined;
    case 'checkbox':
      return typeof value === 'boolean' ? { kind: 'boolean', value } : undefined;
    case 'number':
      return typeof value === 'number' && Number.isFinite(value) ? { kind: 'number', value } : undefined;
    case 'short_text':
    case 'email':
    case 'phone':
    case 'free_form_explore': {
      const text = typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
      return text ? { kind: 'text', value: text } : undefined;
    }
    case 'date_time': {
      const raw = value && typeof value === 'object' && 'value' in value && typeof value.value === 'string' ? value.value : '';
      return /^\d{4}-\d{2}-\d{2}/.test(raw) ? { kind: 'date', value: raw.slice(0, 10) } : { kind: 'present' };
    }
    case 'matrix': {
      if (!value || typeof value !== 'object' || !('selections' in value)) return undefined;
      const selections = Object.fromEntries(Object.entries(value.selections).map(([row, columns]) => [row, Array.isArray(columns) ? columns : [columns]]));
      return Object.keys(selections).length ? { kind: 'matrix', selections } : undefined;
    }
    case 'address':
      return value && typeof value === 'object' && 'country' in value && typeof value.country === 'string' && value.country.trim()
        ? { kind: 'address', country: value.country.trim() } : undefined;
    default:
      return { kind: 'present' };
  }
}

/** Read answers outside `form_answers` validation: uploads and calendar bookings. */
function observeSideChannel(question: AnalyticsQuestion, snapshotField: FormField, row: AnalyticsResponse): ObservedValue | undefined {
  if (question.type === 'file_upload') {
    const ids = row.form_answers?.[snapshotField.name];
    return Array.isArray(ids) && ids.length > 0 && ids.every(id => typeof id === 'string') ? { kind: 'present' } : undefined;
  }
  const bookings = (row.calendar_bookings ?? []).filter(booking => booking.fieldName === snapshotField.name);
  return bookings.length ? { kind: 'present' } : undefined;
}

/**
 * Typed observations for several questions at once, one per response in the
 * given order. Structured answers are validated once per response through the
 * same reader the CSV export uses, so values and labels reconcile.
 */
export function observeQuestions(questions: readonly AnalyticsQuestion[], responses: readonly AnalyticsResponse[], fields: FormField[]): Map<string, Observation[]> {
  const result = new Map<string, Observation[]>(questions.map(question => [question.id, []]));
  for (const row of responses) {
    const schema = schemaOf(row, fields);
    const byId = new Map(schema.map(item => [item.id, item]));
    const answers = new Map(getStructuredResponseAnswers({ form_answers: row.form_answers, form_schema_snapshot: schema }).map(answer => [answer.fieldId, answer]));
    for (const question of questions) {
      const list = result.get(question.id)!;
      const snapshotField = byId.get(question.id);
      if (!snapshotField) {
        list.push({ responseId: row.id, status: 'missing' });
        continue;
      }
      if (valueKind(snapshotField) !== valueKind(question.field)) {
        list.push({ responseId: row.id, status: 'excluded' });
        continue;
      }
      const sideChannel = question.type === 'file_upload' || question.type === 'calendar_booking';
      const answer = answers.get(question.id);
      const value = sideChannel ? observeSideChannel(question, snapshotField, row) : answer && toObserved(question, answer);
      list.push(value ? { responseId: row.id, status: 'answered', value } : { responseId: row.id, status: 'missing' });
    }
  }
  return result;
}

/** Typed observations for one question, one per response in the given order. */
export function observeQuestion(question: AnalyticsQuestion, responses: readonly AnalyticsResponse[], fields: FormField[]): Observation[] {
  return observeQuestions([question], responses, fields).get(question.id)!;
}
